import {assert} from 'Asserts';
import * as FileConstants from 'folder-items/FileConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  EntityAssociations,
  ComplexPolymerConnection,
  ResolvedAttachmentInput,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  ComplexPolymerSchemaConfigGraphQL,
  isOligoConjugateConfigGraphQLType,
  isOligoDuplexConfigGraphQLType,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {ConjugateConnectionMap} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsSummaryUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import {ComplexPolymerComponentEntitiesQuery} from './__generated/ComplexPolymerComponentEntitiesQuery';
import {MoleculeAttachmentForCreateQuery} from './__generated/MoleculeAttachmentForCreateQuery';
import {NucleotideAttachmentForCreateQuery} from './__generated/NucleotideAttachmentForCreateQuery';

export type BioentityOrInaccessibleGraphQL = ComplexPolymerComponentEntitiesQuery['bioentities'][number];
export type CreateComplexPolymerOligoGraphQL = ExtractByTypename<
  BioentityOrInaccessibleGraphQL,
  'SequenceNode'
>;
export type CreateComplexPolymerMoleculeGraphQL = ExtractByTypename<
  BioentityOrInaccessibleGraphQL,
  'MoleculeNode'
>;
export type CreateComplexPolymerMoleculeInComplexPolymerGraphQL = ExtractByTypename<
  MoleculeAttachmentForCreateQuery['moleculeInComplexPolymer'],
  'MoleculeInComplexPolymerNode'
>;
export type CreateComplexPolymerNucleotideInComplexPolymerGraphQL = ExtractByTypename<
  NucleotideAttachmentForCreateQuery['nucleotideInComplexPolymer'],
  'NucleotideInComplexPolymerNode'
>;

export interface CreateComplexPolymerTargetFile extends FileConstants.TargetFile {
  polymerRef?: string;
}

export interface CreateComplexPolymerSchemaTag extends FileConstants.SchemaTag {
  targetFiles?: Array<CreateComplexPolymerTargetFile> | null;
}

export interface ComplexPolymerCreateInputSection {
  sectionHeader: string;
  inputTags: Array<CreateComplexPolymerSchemaTag>;
}

export function getBackboneAndConjugatesTags(
  complexPolymerConfig: ComplexPolymerSchemaConfigGraphQL,
  complexPolymerTags: Array<CreateComplexPolymerSchemaTag>
): {
  backboneTags: Array<CreateComplexPolymerSchemaTag>;
  conjugatesTags: Array<CreateComplexPolymerSchemaTag>;
} {
  let oligoFieldIds: Array<string> = [];
  let conjugatesFieldIds: Array<string> = [];

  if (isOligoConjugateConfigGraphQLType(complexPolymerConfig)) {
    oligoFieldIds = [complexPolymerConfig.oligoSchemaField.id];
    conjugatesFieldIds = complexPolymerConfig.conjugatesSchemaFields.map((field) => field.id);
  } else if (isOligoDuplexConfigGraphQLType(complexPolymerConfig)) {
    oligoFieldIds = [
      complexPolymerConfig.forwardStrandSchemaField.id,
      complexPolymerConfig.reverseStrandSchemaField.id,
    ];
    conjugatesFieldIds = complexPolymerConfig.conjugatesSchemaFields.map((field) => field.id);
  }

  const backboneTags = complexPolymerTags.filter((tag: FileConstants.SchemaTag) =>
    oligoFieldIds.includes(tag.tagSchemaField.apiId)
  );
  const conjugatesTags = complexPolymerTags.filter((tag: FileConstants.SchemaTag) =>
    conjugatesFieldIds.includes(tag.tagSchemaField.apiId)
  );

  return {backboneTags, conjugatesTags};
}

export function getComplexPolymerCreateSections(
  complexPolymerConfig: ComplexPolymerSchemaConfigGraphQL,
  complexPolymerTags: Array<CreateComplexPolymerSchemaTag>
): Array<ComplexPolymerCreateInputSection> {
  const {backboneTags, conjugatesTags} = getBackboneAndConjugatesTags(
    complexPolymerConfig,
    complexPolymerTags
  );

  return [
    {sectionHeader: 'COMPONENTS', inputTags: conjugatesTags},
    {sectionHeader: 'BACKBONE', inputTags: backboneTags},
  ];
}

export function isComponentMolecule(
  bioentity: BioentityOrInaccessibleGraphQL
): bioentity is CreateComplexPolymerMoleculeGraphQL {
  return bioentity.__typename === 'MoleculeNode';
}

export function isComponentOligo(
  bioentity: BioentityOrInaccessibleGraphQL
): bioentity is CreateComplexPolymerOligoGraphQL {
  return bioentity.__typename === 'SequenceNode';
}

export function makeConjugateConnectionsForCreate(
  conjugates: Array<CreateComplexPolymerMoleculeGraphQL | CreateComplexPolymerOligoGraphQL>,
  entityAssociations: EntityAssociations,
  connectionInputs: Array<ComplexPolymerConnection>,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>
): ConjugateConnectionMap {
  // Create a ConjugateConnection corresponding to each conjugate that has been added
  // to the complex polymer, containing any connection information associated with it.
  const conjugateConnectionMap: ConjugateConnectionMap = {};
  conjugates.forEach((conjugate) => {
    const associatedPolymerRefs = entityAssociations[conjugate.id];
    associatedPolymerRefs.forEach((polymerRef) => {
      conjugateConnectionMap[polymerRef] = {
        id: `${conjugate.id}${polymerRef}`,
        entityId: conjugate.id,
        entityName: conjugate.name,
        helmReference: polymerRef,
        residueIndex: null,
        rGroup: null,
        modificationType: null,
        linkedEntityRGroup: null,
        linkedEntityModificationType: null,
        attachmentStyle: null,
        attachmentMolfile: null,
      };
    });
  });

  connectionInputs.forEach((connection) => {
    const conjugateConnection = conjugateConnectionMap[connection.target.polymer_ref];
    conjugateConnectionMap[connection.target.polymer_ref] = {
      ...conjugateConnection,
      residueIndex: connection.target.residue_index,
      rGroup: connection.target.r_group,
      modificationType: connection.target.modification_type,
      linkedEntityResidueIndex: connection.source.residue_index!,
      linkedEntityHelmReference: connection.source.polymer_ref,
      linkedEntityRGroup: connection.source.r_group,
      linkedEntityModificationType: connection.source.modification_type,
      attachmentStyle: connection.attachment_style,
      attachmentMolfile: null,
    };
  });
  resolvedAttachmentInputs.forEach((attachment) => {
    const relatedConjugateConnections = attachment.connections_involved.map(
      (connection) => conjugateConnectionMap[connection.target.polymer_ref]
    );
    relatedConjugateConnections.forEach((conjugateConnection) => {
      conjugateConnectionMap[conjugateConnection.helmReference] = {
        ...conjugateConnection,
        attachmentMolfile: attachment.molfile,
      };
    });
  });

  return conjugateConnectionMap;
}

export function getInitialComplexPolymerTags(
  fields: Array<SchemaField>,
  complexPolymerConfig: ComplexPolymerSchemaConfigGraphQL | null
): Array<CreateComplexPolymerSchemaTag> {
  if (complexPolymerConfig == null) {
    return [];
  }

  if (isOligoConjugateConfigGraphQLType(complexPolymerConfig)) {
    const oligoField = fields.filter(
      (field: SchemaField) => field.apiId === complexPolymerConfig.oligoSchemaField.id
    )[0];
    // We only want to allow one oligo and require it, but all computed field definitions allow multiple values.
    // So we make it act like it's single link and required in this context to enforce that constraint.
    const singleLinkOligoField = {...oligoField, isMulti: false, isRequired: true};
    const conjugatesFields = fields.filter((field: SchemaField) =>
      complexPolymerConfig.conjugatesSchemaFields.some((conjugateField) => conjugateField.id === field.apiId)
    );
    fields = [singleLinkOligoField, ...conjugatesFields];
  } else {
    assert(isOligoDuplexConfigGraphQLType(complexPolymerConfig), 'Expect oligo duplex schema configuration');
    const forwardStrand = fields.filter(
      (field: SchemaField) => field.apiId === complexPolymerConfig.forwardStrandSchemaField.id
    )[0];
    const reverseStrand = fields.filter(
      (field: SchemaField) => field.apiId === complexPolymerConfig.reverseStrandSchemaField.id
    )[0];
    // We only want to allow one oligo and require it, but all computed field definitions allow multiple values.
    // So we make it act like it's single link and required in this context to enforce that constraint.
    const singleLinkForwardStrandField = {...forwardStrand, isMulti: false, isRequired: true};
    const singleLinkReverseStrandField = {...reverseStrand, isMulti: false, isRequired: true};
    const conjugatesFields = fields.filter((field: SchemaField) =>
      complexPolymerConfig.conjugatesSchemaFields.some((conjugateField) => conjugateField.id === field.apiId)
    );

    fields = [singleLinkForwardStrandField, singleLinkReverseStrandField, ...conjugatesFields];
  }

  let tags: Array<CreateComplexPolymerSchemaTag> = [];
  tags = fields.map((field) => ({
    name: field.name,
    value: null,
    tagSchemaField: field,
    targetFiles: [],
  }));

  return tags;
}

export function preprocessConnectionInputsOnUpdate(
  connectionInputs: Array<ComplexPolymerConnection>,
  polymerRefs: Set<string>
): Array<ComplexPolymerConnection> {
  // Remove connections associated with entities that are no longer in the complex polymer
  // (ie if a connection was created with a component that was then deselected from the component
  // selection pane)
  return connectionInputs.filter(
    (connection) =>
      polymerRefs.has(connection.source.polymer_ref) && polymerRefs.has(connection.target.polymer_ref)
  );
}

export function getEntityIdsAndRefsFromTags(complexPolymerTags: Array<CreateComplexPolymerSchemaTag>): {
  ids: Array<string>;
  refs: Array<string>;
} {
  const ids: Array<string> = [];
  const refs: Array<string> = [];
  complexPolymerTags
    .flatMap((tag) => tag.targetFiles ?? [])
    .forEach((targetFile) => {
      ids.push(targetFile.id);
      refs.push(targetFile.polymerRef!);
    });

  return {ids, refs};
}

export function complexPolymerHasAllComponentsForCreate(
  schemaConfiguration: ComplexPolymerSchemaConfigGraphQL,
  complexPolymerComponents: Array<FileConstants.SchemaTag>
): boolean {
  return schemaConfiguration.configValues.every(
    (configValue) =>
      !configValue.isRequired ||
      complexPolymerComponents.some(
        (component) =>
          component.tagSchemaField.apiId === configValue.schemaField.id &&
          component.targetFiles != null &&
          component.targetFiles.length > 0
      )
  );
}
